import {
  BackEndValueType,
  ECardinality,
  ESdChangeReason,
  IAction,
  IActionParameter,
  IConditionModel,
  IConditionalValue,
  ISymbol
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { Comparison } from "./condition";
import { SymbolPicker } from "./symbol-picker";

export interface IParameterConditionValueEditorProps {
  /** The Condition-category symbol owning the `conditionalValue` being edited. */
  symbol: ISymbol;
  valueType?: BackEndValueType;
  targetCardinality?: ECardinality;
  action?: IAction;
  parameter?: IActionParameter;
  disabled?: boolean;
  onChange: (symbol: ISymbol, changeReason?: ESdChangeReason) => void;
}

/**
 * KOA-17739: inline editor for a conditional parameter value — a guard (reusing the standard
 * condition builder) plus "Then" / "Else" value pickers. The value sent at runtime is
 * `condition ? trueSymbol : falseSymbol`. The nested pickers deliberately do not receive
 * `allowConditionValue`, so conditions cannot nest.
 */
export const ParameterConditionValueEditor = (props: IParameterConditionValueEditorProps): JSX.Element => {
  // Mutate the owning symbol in place — the same idiom as SymbolPicker's fixed-value editing:
  // the symbol prop is already a detached clone, and in-place mutation keeps the un-re-rendered
  // picker tree (fixed-value keystrokes do not repaint) reading current state.
  const applyPatch = (patch: Partial<IConditionalValue>, changeReason?: ESdChangeReason): void => {
    props.symbol.conditionalValue = { ...(props.symbol.conditionalValue ?? {}), ...patch };
    props.onChange(props.symbol, changeReason);
  };

  const handleConditionChange = (condition: IConditionModel, changeReason?: ESdChangeReason): void => {
    applyPatch({ condition }, changeReason);
  };

  const handleTrueSymbolChange = (symbol: ISymbol, changeReason?: ESdChangeReason): void => {
    applyPatch({ trueSymbol: symbol }, changeReason);
  };

  const handleFalseSymbolChange = (symbol: ISymbol, changeReason?: ESdChangeReason): void => {
    applyPatch({ falseSymbol: symbol }, changeReason);
  };

  const conditionalValue: IConditionalValue = props.symbol.conditionalValue ?? {};
  const branchLabelStyle: React.CSSProperties = { display: "block", marginTop: 8, marginBottom: 6 };

  return (
    <div data-testid="parameterConditionValueEditor" style={{ width: "100%" }}>
      <Comparison
        action={props.action}
        condition={conditionalValue.condition}
        onChange={handleConditionChange}
        conditionType="parameterValue"
        showHeader={false}
      />
      <span className="k_Regular-G1-Left kuika_rightPanelDIName" style={branchLabelStyle}>
        Then Value (condition is true)
      </span>
      <SymbolPicker
        changeType="onBlur"
        valueType={props.valueType}
        targetCardinality={props.targetCardinality}
        action={props.action}
        parameter={props.parameter}
        symbol={_.cloneDeep(conditionalValue.trueSymbol)}
        onSelectedSymbol={handleTrueSymbolChange}
        disabled={props.disabled}
        data_testid="conditionValueTrue"
      />
      <span className="k_Regular-G1-Left kuika_rightPanelDIName" style={branchLabelStyle}>
        Else Value (condition is false)
      </span>
      <SymbolPicker
        changeType="onBlur"
        valueType={props.valueType}
        targetCardinality={props.targetCardinality}
        action={props.action}
        parameter={props.parameter}
        symbol={_.cloneDeep(conditionalValue.falseSymbol)}
        onSelectedSymbol={handleFalseSymbolChange}
        disabled={props.disabled}
        data_testid="conditionValueFalse"
      />
    </div>
  );
};
